import React, { useCallback, useMemo, useState } from "react";

import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import { ProgramActiveHeader } from "./ProgramActiveHeader";
import { ProgramConfiguration } from "./ProgramConfiguration";
import { ProgramKeyStats } from "./ProgramKeyStats";
import { ProgramPolicies } from "./ProgramPolicies";
import { ProgramProgress } from "./ProgramProgress";
import { QualifiedUsers } from "./QualifiedUsers";
import { type SimScriptStep, useSimScript } from "./useSimScript";
import {
    VIEWING_BUDGET_METER,
    VIEWING_CONFIG,
    VIEWING_KEY_STATS,
    VIEWING_NEXT_DISTRIBUTION,
    VIEWING_PARTICIPANTS,
    VIEWING_POLICIES,
    VIEWING_POLICY_VALUES,
    VIEWING_PROGRAM_NAME,
    VIEWING_STATUS,
    VIEWING_TAGS,
    VIEWING_TIMELINE_METER
} from "./viewingFixtures";

/** No-op — the read view mutates nothing; the create-mode setters/mutators the
 *  reused `ProgramConfiguration` / `ProgramPolicies` props require are wired to
 *  this. */
const NOOP = (): void => {};

/** Cursor-key prefixes for the two tour targets (per row `<prefix>-<id>`). */
const POLICY_EDIT_PREFIX = "view-policy-edit";
const PARTICIPANT_PREFIX = "view-participant";
/** The two rows the tour visits — the first attached policy's Edit affordance
 *  and the first qualified-user row. */
const TOUR_POLICY_ID = VIEWING_POLICIES[0].id;
const TOUR_PARTICIPANT_ID = VIEWING_PARTICIPANTS[0].id;

// ── Tour beat timing (ms) ───────────────────────────────────────────────
/** Lead before a stop's hover — the cursor starts travelling here so it
 *  arrives as the hover paints. Travel is distance-derived + capped at 1s. */
const TRAVEL_MS = 900;
/** How long the cursor rests on each stop, painting its forced hover. */
const HOVER_HOLD_MS = 1200;

/** The one control the tour is currently painting: the policy Edit button, a
 *  participant row, or nothing (resting off-screen). */
type TourStop = "policy-edit" | "participant" | null;

/**
 * Auto-playing, CURSOR-DRIVEN driver for the bare "Viewing Program" doc block.
 *
 * The FIFTH and LAST of the five "+New Program" doc blocks. Unlike the first
 * four (which auto-play a populate / phase chain), this block is a **settled
 * read display** of a *created, active* program — the active read view. Its
 * primary piece of live motion is the **Next Distribution countdown**, which
 * ticks every second via the shared `useCountdown` inside
 * `NextDistributionMeter` — that runs on its own, independent of the cursor.
 *
 * On top of that steady clock, this driver adds a **light, subtle cursor
 * hover-tour** (no clicks — it's a read view): the cursor occasionally drifts
 * across a couple of the interactive read elements — the first attached
 * policy's **Edit** affordance, then a **participant row** — pausing to force
 * its hover so the read-view hover states show, then rests off-screen (cursor
 * hidden) while the countdown keeps ticking. The tour loops occasionally (a
 * long `loopPauseMs`) so the countdown stays the primary motion, not a busy
 * playthrough. Reduced-motion collapses to the still frame with no cursor.
 *
 * "Viewing Program" = the active read view: the active-title header
 * (`ProgramActiveHeader`: title + `StatusBadge` "Active" + tag chips + the live
 * `NextDistributionMeter` on the right), the `ProgramKeyStats` row, the
 * budget/timeline `ProgramProgress` meters, the read-only `ProgramConfiguration`
 * summary (reused from block 1's `!creation` path), the read-only
 * `ProgramPolicies` list (reused from block 2's read path), and the
 * `QualifiedUsers` table.
 *
 * All data is self-contained fixtures ({@link viewingFixtures}); no wallet /
 * RPC / orgApiFetch / roster derivation / router. The block is self-seeded and
 * independent, so a wiki page can drop it alongside the other blocks with no
 * shared state.
 *
 * Drop-in for external (docs/wiki) consumers:
 *
 *   import "@solomon-apps/shared/sim/index.css";
 *   import { ProgramViewSim } from "@solomon-apps/shared/sim";
 *   <ProgramViewSim />
 *
 * No `SimModeProvider` — the countdown is a plain shared hook, not a simulated
 * transaction.
 */
export interface ProgramViewSimProps extends FramableProps {
    /**
     * Pause (ms) the cursor rests off-screen between hover-tours. Long by
     * default (7000) so the tour stays occasional and the Next Distribution
     * countdown remains the primary motion. Set to `-1` to tour once and stop.
     */
    loopPauseMs?: number;
}

function ProgramViewSimView({
    loopPauseMs = 7000
}: ProgramViewSimProps = {}): React.ReactElement {
    // Which read element the cursor is currently force-hovering (one at a time).
    const [stop, setStop] = useState<TourStop>(null);

    const cursor = useSimCursor();
    const { moveTo: moveCursor, hide: hideCursor } = cursor;

    const reset = useCallback((): void => {
        setStop(null);
        hideCursor();
    }, [hideCursor]);

    // Gentle two-stop hover-tour (no clicks — read view):
    //   travel to the first policy's Edit → hover → travel to the first
    //   participant row → hover → rest off-screen (hide), then useSimScript
    //   holds `loopPauseMs` and tours again. The countdown ticks throughout.
    //
    // Timing (mirrors the populate-sims' pin-arrival-to-hover fix): each move
    // pins `durationMs: TRAVEL_MS` so the distance-derived travel doesn't
    // outrun (or lag) the paired hover beat, and the hover beat fires the
    // instant travel completes (`after: TRAVEL_MS`) so the forced hover paints
    // on arrival — no big gap before the highlight lands. The cursor then RESTS
    // on the stop for HOVER_HOLD_MS before the next move.
    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            {
                after: TRAVEL_MS,
                label: "policy-edit:move",
                // Pin travel to the hover's lead so arrival == hover.
                run: (): void =>
                    moveCursor(`${POLICY_EDIT_PREFIX}-${TOUR_POLICY_ID}`, {
                        durationMs: TRAVEL_MS
                    })
            },
            {
                // Paint the hover the instant travel completes (`after` ===
                // TRAVEL_MS) so the row highlight + Edit hover land with the
                // pointer's arrival, no lag.
                after: TRAVEL_MS,
                label: "policy-edit:hover",
                run: (): void => setStop("policy-edit")
            },
            {
                // Rest on the policy row, then travel to the participant row.
                after: HOVER_HOLD_MS,
                label: "participant:move",
                run: (): void => {
                    setStop(null);
                    // Pin travel to the hover's lead so arrival == hover.
                    moveCursor(`${PARTICIPANT_PREFIX}-${TOUR_PARTICIPANT_ID}`, {
                        durationMs: TRAVEL_MS
                    });
                }
            },
            {
                // Paint the row hover on arrival (`after` === TRAVEL_MS).
                after: TRAVEL_MS,
                label: "participant:hover",
                run: (): void => setStop("participant")
            },
            // Rest: drop the forced hover + fade the cursor out before the
            // (long) loop pause, so the countdown carries the frame alone.
            {
                after: HOVER_HOLD_MS,
                label: "rest",
                run: (): void => {
                    setStop(null);
                    hideCursor();
                }
            }
        ],
        [moveCursor, hideCursor]
    );

    useSimScript({ reset, steps, loopPauseMs });

    return (
        <div ref={cursor.containerRef} className="relative">
            {/* Active-title header — title + StatusBadge "Active" + tag chips,
                with the live Next Distribution countdown meter on the right. */}
            <ProgramActiveHeader
                name={VIEWING_PROGRAM_NAME}
                statusLabel={VIEWING_STATUS.label}
                statusState={VIEWING_STATUS.state}
                statusLabelClassName={VIEWING_STATUS.labelClassName}
                tags={VIEWING_TAGS}
                nextDistribution={VIEWING_NEXT_DISTRIBUTION}
            />

            {/* Key-stats row — held / accrued / projected / distributed /
                users. */}
            <ProgramKeyStats stats={VIEWING_KEY_STATS} />

            {/* Budget + timeline progress meters. */}
            <ProgramProgress
                budgetMeter={VIEWING_BUDGET_METER}
                timelineMeter={VIEWING_TIMELINE_METER}
            />

            {/* Read-only Configuration summary — reuses block 1's View in its
                `!creation` path (status: "active"); the draft setters are
                no-ops (the read view never mutates). */}
            <ProgramConfiguration
                program={VIEWING_CONFIG}
                programType="fixed"
                setProgramType={NOOP}
                budgetMode="fixed"
                setBudgetMode={NOOP}
                apyMode={(VIEWING_CONFIG.baseApy ?? 0) > 0 ? "base" : "earned"}
                setApyMode={NOOP}
                baseApy={VIEWING_CONFIG.baseApy ?? 0}
                setBaseApy={NOOP}
                userPct={VIEWING_CONFIG.userPct ?? 80}
                setUserPct={NOOP}
                periodDays={VIEWING_CONFIG.periodDays ?? 0}
                setPeriodDays={NOOP}
                settlementCycleLength={
                    VIEWING_CONFIG.settlementCycleLength ?? 0
                }
                setSettlementCycleLength={NOOP}
                settlementCycleUnit={
                    VIEWING_CONFIG.settlementCycleUnit ?? "days"
                }
                setSettlementCycleUnit={NOOP}
                budget={VIEWING_CONFIG.budget ?? 0}
                setBudget={NOOP}
            />

            {/* Read-only Policies list — reuses block 2's View in its read path
                (status: "active"); the mutators are no-ops. */}
            <ProgramPolicies
                program={{ status: "active" }}
                policies={VIEWING_POLICIES}
                policyValues={VIEWING_POLICY_VALUES}
                onAddPolicy={NOOP}
                onRemovePolicy={NOOP}
                onChangePolicyValue={NOOP}
                demo={{
                    editHoverId: stop === "policy-edit" ? TOUR_POLICY_ID : null,
                    editCursorKeyPrefix: POLICY_EDIT_PREFIX
                }}
            />

            {/* Qualified Users table. */}
            <QualifiedUsers
                participants={VIEWING_PARTICIPANTS}
                demo={{
                    hoverId:
                        stop === "participant" ? TOUR_PARTICIPANT_ID : null,
                    cursorKeyPrefix: PARTICIPANT_PREFIX
                }}
            />
            {cursor.layer}
        </div>
    );
}

/**
 * Settled active read view of a created program. Add `framed` to wrap it in
 * its own fixed-height {@link SimFrame} (the block owns its max height); omit
 * it to render bare.
 */
export const ProgramViewSim = framable(ProgramViewSimView, "ProgramViewSim");
